import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
    MessageCircle,
    Send,
    Loader2,
    Check,
    CheckCheck,
    Inbox as InboxIcon,
} from "lucide-react";
import { toast } from "sonner";

function timeAgo(iso) {
    if (!iso) return "";
    const t = new Date(iso).getTime();
    const s = Math.max(0, (Date.now() - t) / 1000);
    if (s < 60) return "just now";
    if (s < 3600) return `${Math.round(s / 60)} min ago`;
    if (s < 86400) return `${Math.round(s / 3600)} h ago`;
    return new Date(iso).toLocaleDateString();
}

export default function InstructorInbox() {
    const [threads, setThreads] = useState([]);
    const [selected, setSelected] = useState(null);
    const [messages, setMessages] = useState([]);
    const [loadingList, setLoadingList] = useState(true);
    const [loadingThread, setLoadingThread] = useState(false);
    const [text, setText] = useState("");
    const [sending, setSending] = useState(false);
    const scrollRef = useRef(null);
    const pollRef = useRef(null);

    const refreshThreads = async () => {
        try {
            const { data } = await api.get("/instructor/threads");
            setThreads(data || []);
        } catch {
            toast.error("Could not load your inbox.");
        } finally {
            setLoadingList(false);
        }
    };

    const openThread = async (t) => {
        setSelected(t);
        setLoadingThread(true);
        try {
            const { data } = await api.get(`/threads/${t.id}/messages`);
            setMessages(data.messages || []);
            setSelected(data.thread);
            // Refresh list to clear the unread badge
            refreshThreads();
        } catch {
            toast.error("Could not open conversation.");
        } finally {
            setLoadingThread(false);
        }
    };

    useEffect(() => {
        refreshThreads();
        const id = setInterval(refreshThreads, 30000);
        return () => clearInterval(id);
    }, []);

    // Poll the open thread
    useEffect(() => {
        if (!selected) return;
        pollRef.current = setInterval(async () => {
            try {
                const { data } = await api.get(`/threads/${selected.id}/messages`);
                setMessages(data.messages || []);
            } catch {
                /* silent */
            }
        }, 8000);
        return () => {
            if (pollRef.current) clearInterval(pollRef.current);
        };
    }, [selected?.id]);

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [messages, selected]);

    const send = async (e) => {
        e?.preventDefault();
        const v = text.trim();
        if (!v || sending || !selected) return;
        setSending(true);
        const optimistic = {
            id: `temp-${Date.now()}`,
            sender_role: "instructor",
            content: v,
            created_at: new Date().toISOString(),
            read_at: null,
        };
        setMessages((m) => [...m, optimistic]);
        setText("");
        try {
            await api.post(`/threads/${selected.id}/messages`, { content: v });
            const { data } = await api.get(`/threads/${selected.id}/messages`);
            setMessages(data.messages || []);
            refreshThreads();
        } catch {
            toast.error("Could not send your reply.");
            setMessages((m) => m.filter((x) => x.id !== optimistic.id));
            setText(v);
        } finally {
            setSending(false);
        }
    };

    const totalUnread = threads.reduce(
        (sum, t) => sum + (t.unread_for_instructor || 0),
        0,
    );

    return (
        <div className="max-w-6xl mx-auto p-6 space-y-6" data-testid="instructor-inbox">
            <header className="flex items-center gap-3">
                <InboxIcon className="h-6 w-6 text-primary" />
                <div>
                    <h1 className="font-display text-3xl text-primary">Student Inbox</h1>
                    <p className="text-sm text-muted-foreground">
                        Answer student questions across your programs.
                        {totalUnread > 0 && (
                            <span className="ml-2 inline-flex items-center gap-1 text-destructive font-semibold">
                                · {totalUnread} unread
                            </span>
                        )}
                    </p>
                </div>
            </header>

            <div className="grid md:grid-cols-[360px,1fr] gap-4">
                {/* Thread list */}
                <Card className="border-border h-[70vh] overflow-hidden flex flex-col">
                    <CardContent className="p-0 flex-1 overflow-y-auto">
                        {loadingList && (
                            <div className="flex justify-center py-10">
                                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                            </div>
                        )}
                        {!loadingList && threads.length === 0 && (
                            <div className="text-center text-sm text-muted-foreground py-12 px-4">
                                <MessageCircle className="h-8 w-8 mx-auto text-muted-foreground/40 mb-2" />
                                No student questions yet. When a student asks, they&rsquo;ll appear
                                here.
                            </div>
                        )}
                        {threads.map((t) => {
                            const active = selected?.id === t.id;
                            const unread = t.unread_for_instructor || 0;
                            return (
                                <button
                                    key={t.id}
                                    type="button"
                                    onClick={() => openThread(t)}
                                    className={`w-full text-left px-4 py-3 border-b border-border hover:bg-secondary/40 transition ${
                                        active ? "bg-secondary/60" : ""
                                    }`}
                                    data-testid="instructor-thread-row"
                                >
                                    <div className="flex items-center gap-2">
                                        <div className="font-semibold text-sm text-foreground truncate flex-1">
                                            {t.student?.name || t.student?.email || "Student"}
                                        </div>
                                        {unread > 0 && (
                                            <span className="h-5 min-w-5 px-1.5 rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold flex items-center justify-center">
                                                {unread > 9 ? "9+" : unread}
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-xs text-muted-foreground truncate mt-0.5">
                                        {t.program_title}
                                    </div>
                                    <div className="text-[11px] text-muted-foreground mt-1">
                                        {timeAgo(t.last_message_at)}
                                    </div>
                                </button>
                            );
                        })}
                    </CardContent>
                </Card>

                {/* Conversation */}
                <Card className="border-border h-[70vh] overflow-hidden flex flex-col">
                    {selected ? (
                        <>
                            <div className="px-4 py-3 border-b border-border flex-shrink-0">
                                <div className="font-semibold text-sm">
                                    {selected.student?.name || selected.student?.email}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                    {selected.program_title}
                                </div>
                            </div>
                            <div
                                ref={scrollRef}
                                className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-gradient-to-b from-secondary/30 to-card"
                            >
                                {loadingThread && messages.length === 0 && (
                                    <div className="flex justify-center py-8">
                                        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                                    </div>
                                )}
                                {messages.map((m) => {
                                    const mine = m.sender_role === "instructor";
                                    return (
                                        <div
                                            key={m.id}
                                            className={`flex ${mine ? "justify-end" : "justify-start"}`}
                                        >
                                            <div
                                                className={`max-w-[75%] rounded-2xl px-3.5 py-2 text-sm leading-relaxed break-words ${
                                                    mine
                                                        ? "bg-primary text-primary-foreground rounded-br-sm"
                                                        : "bg-accent/20 text-foreground rounded-bl-sm border border-accent/30"
                                                }`}
                                            >
                                                <div className="whitespace-pre-wrap">{m.content}</div>
                                                <div
                                                    className={`text-[10px] mt-1 flex items-center gap-1 ${
                                                        mine ? "text-primary-foreground/70" : "text-muted-foreground"
                                                    }`}
                                                >
                                                    {new Date(m.created_at).toLocaleString([], {
                                                        hour: "2-digit",
                                                        minute: "2-digit",
                                                    })}
                                                    {mine &&
                                                        (m.read_at ? (
                                                            <CheckCheck className="h-3 w-3" />
                                                        ) : (
                                                            <Check className="h-3 w-3" />
                                                        ))}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            <form
                                onSubmit={send}
                                className="border-t border-border px-3 py-3 flex gap-2 flex-shrink-0"
                            >
                                <textarea
                                    value={text}
                                    onChange={(e) => setText(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter" && !e.shiftKey) {
                                            e.preventDefault();
                                            send();
                                        }
                                    }}
                                    placeholder="Type your reply…"
                                    className="flex-1 resize-none rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/30 min-h-[40px] max-h-32"
                                    rows={1}
                                    data-testid="instructor-reply-input"
                                />
                                <Button
                                    type="submit"
                                    size="icon"
                                    disabled={!text.trim() || sending}
                                    className="h-10 w-10"
                                    data-testid="instructor-reply-send"
                                >
                                    {sending ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                        <Send className="h-4 w-4" />
                                    )}
                                </Button>
                            </form>
                        </>
                    ) : (
                        <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground">
                            Select a conversation to reply.
                        </div>
                    )}
                </Card>
            </div>
        </div>
    );
}
